/**
 * =========================================================================================
 *     DOBLER DATA SOLUTIONS, LLC - PROPRIETARY AND CONFIDENTIAL
 * =========================================================================================
 * The source code contained herein is owned by Dobler Data Solutions, LLC. The source code
 * and all other copyrightable materials related thereto, including without limitation, all
 * text, logos, designs, graphics, other files, and other code, is protected under Title 17
 * of the United States Code. Any use, including any commercial use, distribution,
 * republication, or display of the source code or any other copyrightable materials related
 * thereto is strictly prohibited. Copyright © 2026 Dobler Data Solutions, LLC.
 * ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File Name       :   feature-switches.ts
 *     Description     :   Shared contract for spec 044 — the visible feature switches: the 15 switch
 *                         keys (13 switches and 2 dev test aids) with the display names the store is
 *                         seeded with, the two "not built" items, the API shapes of contracts/api.md
 *                         §1–§3, its refusal codes, and the three refusal messages of §4.
 *     Module          :   044-visible-feature-switches (shared)
 *     Component Type  :   Model
 * -----------------------------------------------------------------------------------------
 *     CHANGE HISTORY
 * -----------------------------------------------------------------------------------------
 *     Version     Date            Author              Description
 * -----------------------------------------------------------------------------------------
 *     1.0         2026-10-25      Peter Dobler        Initial creation (spec 044 T002)
 *     1.1         2026-10-28      Peter Dobler        The two legacy SSIS conversion switches (owner: "two new
 *                                                     switches to turn off the ssis legacy process")
 * -----------------------------------------------------------------------------------------
 *     DEPENDENCIES
 * -----------------------------------------------------------------------------------------
 *     External        :   (none)
 *     Internal        :   (none)
 *     EXPORTS
 * -----------------------------------------------------------------------------------------
 *     FEATURE_SWITCH_KEYS FeatureSwitchKey FEATURE_SWITCH_DISPLAY_NAMES isFeatureSwitchKey
 *     SwitchRuntime FeatureSwitch NOT_BUILT NotBuiltItem SwitchListResponse SwitchChangeSource
 *     SwitchChange SwitchHistoryResponse SwitchChangeRequest SWITCH_REASON_MIN_LENGTH
 *     VALUE_SWITCHES SwitchErrorCode parseTestbedNpis switchOffMessage switchMissingMessage
 *     switchUnreadableMessage
 * =========================================================================================
 */

// ---------------------------------------------------------------------------------------------
// The switches (data-model.md, the seed)
// ---------------------------------------------------------------------------------------------

/**
 * The 14 switch keys, in the header's order: the 12 switches, then the 2 dev test aids. The two legacy SSIS
 * conversion switches (2026-10-28) follow their Python twins; AddLegacyConversionSwitches.sql seeds them.
 * `DATAMAVE_SYNC_WIRING` left with the legacy DataMave sync (spec 047 T082). Its row and history stay in the
 * store as the record, and a key off this list is not read or shown.
 */
export const FEATURE_SWITCH_KEYS = [
  "ORG_MIGRATION_WIRING",
  "DEPLOY_LIVE_WIRING",
  "PUSH_IMAGES_WIRING",
  "IMAGE_REMOVAL_WIRING",
  "UNIVERSAL_INGEST_WIRING",
  "UNIVERSAL_CONVERSION_PYTHON_WIRING",
  "UNIVERSAL_CONVERSION_LEGACY_WIRING",
  "PERSONALMED_CONVERSION_PYTHON_WIRING",
  "PERSONALMED_CONVERSION_LEGACY_WIRING",
  "DATAMAVE_FORMULARY_WIRING",
  "SCHEMA_SYNC_WIRING",
  "PROCESSING_LIVE_WIRING_ENABLED",
  "ALLOW_STAGE_SKIP",
  "PERSONALMED_TESTBED_NPIS",
] as const;
export type FeatureSwitchKey = (typeof FEATURE_SWITCH_KEYS)[number];

/**
 * The display names the migration seeds (data-model.md). A runtime names a switch from its stored
 * row; this map is the name when the row is missing or the store cannot be read (contracts §4).
 * `AddFeatureSwitches.sql` is checked against it by test.
 */
export const FEATURE_SWITCH_DISPLAY_NAMES: Readonly<Record<FeatureSwitchKey, string>> = {
  ORG_MIGRATION_WIRING: "Remove Pharmacy",
  DEPLOY_LIVE_WIRING: "Deploy to Live",
  PUSH_IMAGES_WIRING: "Push Images",
  IMAGE_REMOVAL_WIRING: "Image Removal",
  UNIVERSAL_INGEST_WIRING: "Universal Lakehouse Load",
  UNIVERSAL_CONVERSION_PYTHON_WIRING: "Universal Conversion (Python)",
  UNIVERSAL_CONVERSION_LEGACY_WIRING: "Universal Conversion (legacy SSIS)",
  PERSONALMED_CONVERSION_PYTHON_WIRING: "PersonalMed Conversion (Python)",
  PERSONALMED_CONVERSION_LEGACY_WIRING: "PersonalMed Conversion (legacy SSIS)",
  DATAMAVE_FORMULARY_WIRING: "DataMave Formulary Transfer",
  SCHEMA_SYNC_WIRING: "Sync Migration Schemas",
  PROCESSING_LIVE_WIRING_ENABLED: "Orchestrator Live Execution",
  ALLOW_STAGE_SKIP: "Stage Skip (dev test aid)",
  PERSONALMED_TESTBED_NPIS: "Testbed NPIs (dev test aid)",
};

/** True when `value` is one of the 14 switch keys. */
export function isFeatureSwitchKey(value: string): value is FeatureSwitchKey {
  return (FEATURE_SWITCH_KEYS as readonly string[]).includes(value);
}

/** Where a switch's decision is applied. */
export type SwitchRuntime = "control-plane" | "worker" | "orchestrator";

/** One switch as `GET /api/v1/switches` returns it (contracts §1). `on` is the effective state. */
export interface FeatureSwitch {
  readonly key: FeatureSwitchKey;
  readonly displayName: string;
  readonly description: string;
  readonly runtime: SwitchRuntime;
  readonly on: boolean;
  readonly devOnly: boolean;
  readonly value: string | null;
  readonly version: number;
  readonly lastChangedBy: string;
  readonly lastChangedAtUtc: string;
}

/** An item that guards code never built: shown, never changeable (FR-002). */
export interface NotBuiltItem {
  readonly key: string;
  readonly displayName: string;
  readonly why: string;
}

/**
 * The three source constants that guard unbuilt code (data-model.md, "Not built"). The third,
 * `LIVE_WIRING_ENABLED`, was found at implementation (research R-015): the spec had named two.
 */
export const NOT_BUILT: readonly NotBuiltItem[] = [
  {
    key: "PROVISIONING_LIVE_WIRING_ENABLED",
    displayName: "Environment provisioning",
    why: "The live provisioning path was never built (orchestrator/reservation.ts).",
  },
  {
    key: "LIFECYCLE_LIVE_WIRING_ENABLED",
    displayName: "Lifecycle buttons",
    why: "Clean Out and Reload UAT were never built; the buttons send no request (frontend/src/api/lifecycleActions.ts).",
  },
  {
    key: "LIVE_WIRING_ENABLED",
    displayName: "Remove Pharmacy legacy actions",
    why:
      "The legacy Remove Pharmacy actions of spec 007 are mapped and audited but never called; only the organization " +
      "back-out and the staging cleanup are built, under the Remove Pharmacy switch (backend/src/db/removalExecution.ts).",
  },
];

/** `GET /api/v1/switches` (contracts §1). */
export interface SwitchListResponse {
  /** The database's one dev rule (`fn_MigrationControlIsDevServer`); the banner shows the test aids only when true. */
  readonly devTestAidsAllowed: boolean;
  readonly switches: readonly FeatureSwitch[];
  readonly notBuilt: readonly NotBuiltItem[];
}

// ---------------------------------------------------------------------------------------------
// History (contracts §2)
// ---------------------------------------------------------------------------------------------

export type SwitchChangeSource = "console" | "outside-console" | "seed";

/** One history entry, newest first in a response. */
export interface SwitchChange {
  readonly version: number;
  readonly wasOn: boolean | null;
  readonly on: boolean;
  readonly wasValue: string | null;
  readonly value: string | null;
  readonly changedBy: string;
  /** The database login that made the change, on every entry (data-model.md, S1). */
  readonly dbLogin: string;
  readonly changedAtUtc: string;
  readonly reason: string | null;
  readonly source: SwitchChangeSource;
}

export interface SwitchHistoryResponse {
  readonly key: FeatureSwitchKey;
  readonly changes: readonly SwitchChange[];
}

// ---------------------------------------------------------------------------------------------
// A change (contracts §3)
// ---------------------------------------------------------------------------------------------

/** `PUT /api/v1/switches/:key`. `value` only for a switch that carries one. */
export interface SwitchChangeRequest {
  readonly on: boolean;
  readonly value?: string;
  readonly typedName: string;
  readonly reason: string;
  readonly expectedVersion: number;
}

/** A reason shorter than this, after trimming, is refused (`REASON_REQUIRED`). */
export const SWITCH_REASON_MIN_LENGTH = 10;

/** The switches that carry a value; any other refuses one (`VALUE_NOT_ALLOWED`). */
export const VALUE_SWITCHES: readonly FeatureSwitchKey[] = ["PERSONALMED_TESTBED_NPIS"];

/** The refusal codes of contracts §1–§3. */
export type SwitchErrorCode =
  | "INVALID_REQUEST"
  | "FORBIDDEN"
  | "UNKNOWN_SWITCH"
  | "STALE_SWITCH"
  | "TYPED_NAME_MISMATCH"
  | "REASON_REQUIRED"
  | "NO_CHANGE"
  | "VALUE_NOT_ALLOWED"
  | "INVALID_VALUE"
  | "DEV_ONLY_SWITCH"
  | "SWITCHES_UNAVAILABLE";

// ---------------------------------------------------------------------------------------------
// The refusal messages every runtime gives (contracts §4)
// ---------------------------------------------------------------------------------------------

/**
 * The testbed NPI list (`PERSONALMED_TESTBED_NPIS`' value): comma-separated 10-digit NPIs, de-duplicated.
 * NULL or blank ⇒ an empty list, which refuses every testbed write. A malformed entry ⇒ `null`: the whole
 * list is refused rather than an entry dropped, because the list decides which migrations the testbed may
 * overwrite and a typo must not silently narrow it (the rule of spec 041 data-model §5, moved here).
 */
export function parseTestbedNpis(value: string | null): readonly string[] | null {
  const tokens = (value ?? "")
    .split(",")
    .map((token) => token.trim())
    .filter((token) => token.length > 0);
  if (tokens.some((token) => !/^\d{10}$/.test(token))) return null;
  return [...new Set(tokens)];
}

/** The switch is off (FR-015). */
export function switchOffMessage(displayName: string): string {
  return `${displayName} is off — change it on the Switches page.`;
}

/** The switch's row is missing from the store. */
export function switchMissingMessage(displayName: string): string {
  return `${displayName} is missing from the switch store, so it is treated as off.`;
}

/** The store cannot be read (FR-006). */
export function switchUnreadableMessage(displayName: string): string {
  return `The switch store cannot be read, so ${displayName} is treated as off. Check MigrationControlDB.`;
}
